import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useRecoilState, useSetRecoilState } from 'recoil';
import { tocNavigationState } from 'recoils/common/menuState';

/**
 * 페이지 수준에서 목차(TOC) 아이템과 제목을 설정하기 위한 커스텀 훅
 * @param {string} title 목차 상단에 표시될 제목
 * @param {Array} items [{ id, label, href }] 형태의 객체 배열
 */
export const useSetToc = (title, items) => {
  const setTocNavigation = useSetRecoilState(tocNavigationState);

  useEffect(() => {
    setTocNavigation((prev) => ({
      title: title !== undefined ? title : prev.title,
      items: items !== undefined ? items : prev.items,
    }));

    let timeoutId1;
    let timeoutId2;
    let scrollRestorationPrev;

    // 해시가 있을 경우 초기 로드 시 해당 위치로 부드럽게 스크롤
    if (window.location.hash && items && items.length > 0) {
      try {
        const hashId = decodeURIComponent(window.location.hash.substring(1));
        const hasItem = items.some((item) => item.id === hashId);

        if (hasItem) {
          if ('scrollRestoration' in window.history) {
            scrollRestorationPrev = window.history.scrollRestoration;
            window.history.scrollRestoration = 'manual';
          }

          const scrollToHash = () => {
            const element = document.getElementById(hashId);
            if (element) {
              const headerHeight = 90;
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.scrollY - headerHeight;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
              });
            }
          };

          // 즉시 실행 및 레이아웃 렌더링 지연을 고려해 100ms, 300ms 후 재실행
          scrollToHash();
          timeoutId1 = setTimeout(scrollToHash, 100);
          timeoutId2 = setTimeout(scrollToHash, 300);
        }
      } catch (e) {
        console.error('Invalid hash', e);
      }
    } else if (!window.location.hash) {
      // 해시가 없으면 상단으로 (필요 시) - 제거 (입력 시 리렌더링되며 맨 위로 강제 스크롤되는 이슈 해결)
      // window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // 목차 초기화
    return () => {
      setTocNavigation({ title: '', items: [] });
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
      if (scrollRestorationPrev && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = scrollRestorationPrev;
      }
    };
  }, [title, items, setTocNavigation]);
};

/**
 * 현재 활성화된 TOC 섹션을 관리하고 스크롤 감지 로직을 제공하는 커스텀 훅
 * @returns {Object} { activeId, scrollToSection, scrollToTop, title, items }
 */
export const useActiveToc = () => {
  const [tocNavigation] = useRecoilState(tocNavigationState);
  const { title, items } = tocNavigation;

  const [activeId, setActiveId] = useState('');
  const isClickScrolling = useRef(false);
  const scrollEndTimer = useRef(null);

  const handleScroll = useCallback(() => {
    if (!items || items.length === 0) return;

    // 1. 클릭으로 인한 스크롤 중이면 스크롤 감지 로직 건너뜜
    if (isClickScrolling.current) return;

    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // 2. 화면 최하단 도달 시 마지막 아이템 활성화
    if (windowHeight + scrollY >= documentHeight - 50) {
      setActiveId(items[items.length - 1].id);
      return;
    }

    // 3. 스크롤이 0이면 초기화
    if (scrollY === 0) {
      setActiveId('');
      return;
    }

    // 4. 현재 뷰포트 상단(헤더 아래)에 걸친 섹션 찾기
    const headerOffset = 100;
    let currentActiveId = '';

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const element = document.getElementById(item.id);
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top <= headerOffset + 20) {
          currentActiveId = item.id;
        } else {
          break;
        }
      }
    }

    if (currentActiveId) {
      setActiveId(currentActiveId);
    } else {
      setActiveId('');
    }
  }, [items]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
    };
  }, [handleScroll]);

  /**
   * 클릭 동작에 의한 수동 활성화 처리 (스크롤 감지 일시 정지용)
   */
  const handleManualActivate = useCallback((id) => {
    isClickScrolling.current = true;
    if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);

    setActiveId(id);

    scrollEndTimer.current = setTimeout(() => {
      isClickScrolling.current = false;
    }, 1000);
  }, []);

  /**
   * 클릭 동작에 의한 수동 활성화 및 해당 섹션으로 스크롤 이동 처리
   */
  const scrollToSection = useCallback(
    (id) => {
      const element = document.getElementById(id);
      if (!element) return;

      // 1. 수동 활성화 처리
      handleManualActivate(id);

      // 2. 헤더 높이(90px)를 고려하여 부드럽게 스크롤 이동
      const headerHeight = 90;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      // URL 해시 업데이트
      window.history.pushState(null, '', `#${id}`);
    },
    [handleManualActivate]
  );

  /**
   * 상단으로 스크롤 이동 및 활성 상태 초기화
   */
  const scrollToTop = useCallback(() => {
    handleManualActivate(''); // 활성 상태 초기화 및 스크롤 감지 잠시 중단

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    // URL 해시 제거
    window.history.pushState(null, '', window.location.pathname);
  }, [handleManualActivate]);

  return { activeId, scrollToSection, scrollToTop, title, items };
};

export default useSetToc;
